import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { HTML_LINK_TARGET } from '../../../../constants/html'
import { PROFILE_ROUTE_NAMES } from '../../../../constants/route'
import { useLinkLikeClick, useNotification } from '../../../../hooks'
import { stopPropagation } from '../../../../utils/events'
import { MINIMIZED_MODAL_SCOPE } from '../../../Modal'
import {
  deleteEmailMessage,
  resendEmail,
  toggleEmailRead,
} from '../../Email.api'
import {
  EMAIL_ACTION_ATTACH_REQUEST,
  EMAIL_ACTION_COPY_TO,
  EMAIL_ACTION_COPY_TO_DELETE,
  EMAIL_ACTION_CREATE_SERVICE_REQUEST,
  EMAIL_ACTION_VIEW_ENTIRE_MESSAGE,
  EMAIL_PROFILE_ACTION_ADD_NOTE,
  EMAIL_PROFILE_ACTION_DOWNLOAD_FILES,
  EMAIL_PROFILE_ACTION_MESSAGE_RESEND,
} from '../../Email.constants'
import {
  EmailAddNoteActionModal,
  getEmailAddNoteInitialValues,
} from '../../EmailAddNoteActionModal'
import { useEmailAttachRequest } from '../useEmailAttachRequest'
import { useEmailCopyTo } from '../useEmailCopyTo'
import { useEmailCreateRequestAction } from '../useEmailCreateRequestAction'
import { useEmailFilesDownloadAll } from '../useEmailFilesDownloadAll'
import { useEmailProfileActions } from '../useEmailProfileActions'
import { useEmailProfileContext } from '../useEmailProfileContext'

import {
  EMAIL_PROFILE_ACTIONS_MESSAGE_ERROR_TITLE,
  EMAIL_PROFILE_ACTIONS_MESSAGE_SUCCESS_TITLE,
} from './useEmailProfileActionsMessage.constants'

/**
 * Hook that extends base email profile actions with message-specific actions
 * such as resend, create service request, attach request, copy to entity,
 * remove copied entity, and view entire message.
 *
 * @param {object} [params]
 * @param {({ data: object, uuid: string }) => Promise<unknown>} [params.copyToApi]
 *    Override for the "Copy to" API call. Defaults to the global
 *    `copyEmailToEntity`. Pass a request-scoped variant when the message is
 *    rendered inside a service-request context.
 * @param {({ data: object, uuid: string }) => Promise<unknown>} [params.deleteCopiedEntityApi]
 *    Override for the "Remove copied entity" API call. Defaults to the global
 *    `deleteEmailCopiedEntity`. Pass a request-scoped variant when the
 *    message is rendered inside a service-request context.
 * @param {({ action: string, data: object, id: string }) => Promise<unknown>} [params.deleteMessageApi]
 *    Override for the delete-message API used by the archive/delete action.
 *    Defaults to the global `deleteEmailMessage`. Pass a request-scoped
 *    variant to keep deletes inside the current service request.
 * @param {({ additionalParams?: string, id: string }) => string} [params.getPrintPageLink]
 *    Builds the message print-page URL. Defaults to
 *    `PROFILE_ROUTE_NAMES.emailMessagePrint`. Override to point at a
 *    request-scoped print route.
 * @param {({ additionalParams?: string, id: string }) => string} [params.getRawPageLink]
 *    Builds the "view entire message" raw URL. Defaults to
 *    `PROFILE_ROUTE_NAMES.emailMessageRaw`. Override to point at a
 *    request-scoped raw route.
 * @param {(item: object) => string[]} [params.getExcludeIds]
 *    Builds the list of service-request ids to exclude from the
 *    "Attach to service request" picker. Defaults to ids derived from
 *    `linked_requests`. Override to also exclude the current request
 *    when the message is rendered inside a service-request context.
 * @param {object} [params.item]
 *    Email message item. If not provided, the first message from context is used.
 * @param {string} [params.minimizedModalScope=MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE]
 *    Scope identifying the subtree hosting this message. Tags the minimizable
 *    modals opened from it so `confirmBeforeAction` only prompts for hosts the
 *    attempted action would actually unmount.
 * @param {(uuid: string) => Promise<unknown>} [params.resendApi]
 *    Override for the resend API. Defaults to the global `resendEmail`.
 *    Pass a request-scoped variant to keep the resend within the current
 *    service request.
 * @param {boolean} [params.shouldNavigateToList]
 *    Whether to navigate to mail list after actions such as archive or mark as unread.
 * @param {() => void} [params.onForward]
 *    Called when forward action is triggered.
 * @param {() => void} [params.onReply]
 *    Called when reply action is triggered.
 * @param {() => void} [params.onReplyAll]
 *    Called when reply all action is triggered.
 * @param {() => void} [params.onSuccessArchive]
 *    Called after an archive removes the whole thread from the list (the last
 *    message was archived) and navigation to the mail list is disabled.
 * @param {({ id?: string, unread: boolean }) => void} [params.onSuccessMarkAsUnread]
 *    Called after successful mark as unread action.
 * @param {({ id?: string, messagesLength?: number }) => void} [params.onSuccessUpdate]
 *    Called after successful update entity.
 */
export function useEmailProfileActionsMessage({
  copyToApi,
  deleteCopiedEntityApi,
  deleteMessageApi = deleteEmailMessage,
  getExcludeIds,
  getPrintPageLink = PROFILE_ROUTE_NAMES.emailMessagePrint,
  getRawPageLink = PROFILE_ROUTE_NAMES.emailMessageRaw,
  item,
  minimizedModalScope = MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
  resendApi = resendEmail,
  shouldNavigateToList,
  onForward,
  onReply,
  onReplyAll,
  onSuccessArchive,
  onSuccessMarkAsUnread,
  onSuccessUpdate,
} = EMPTY_OBJECT) {
  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const {
    handleCloseModal,
    handleReloadEntity,
    handleReloadMessages,
    handleUpdateMessage,
    messages,
    setModal,
    setPerformingAction,
  } = useEmailProfileContext()

  async function handleMarkAsUnread({ unread, uuid }) {
    return toggleEmailRead({ read: !unread, single: true, uuid })
  }

  function handleSuccessActionRequest() {
    handleCloseModal()

    handleReloadEntity()

    onSuccessUpdate?.()
  }

  function handleSuccessAttachRequest(action, notifications) {
    handleSuccessActionRequest()

    notifications.forEach(handleAddSuccessNotification)
  }

  const { handleClickAction, handleUpdate } = useEmailProfileActions({
    getPrintPageLink,
    minimizedModalScope,
    shouldNavigateToList,
    onForward,
    onReply,
    onReplyAll,
    onSuccessArchive,
    onSuccessMarkAsUnread,
    onSuccessUpdate,
    onUnread: handleMarkAsUnread,
    onUpdate: handleUpdateMessage,
  })

  const { handleOpenRequestCreationModal, requestCreationModal } =
    useEmailCreateRequestAction({
      minimizedModalScope,
      onSuccess: handleSuccessActionRequest,
    })

  const { handleOpenAttachServiceRequestModal } = useEmailAttachRequest({
    successTitle: t(
      EMAIL_PROFILE_ACTIONS_MESSAGE_SUCCESS_TITLE[EMAIL_ACTION_ATTACH_REQUEST]
    ),
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
    onSuccessAction: handleSuccessAttachRequest,
  })

  const { handleClick } = useLinkLikeClick()

  const message = item || messages?.[0]
  const id = message?.uuid

  const { handleClickDownloadAll: handleClickDownloadAllMessage } =
    useEmailFilesDownloadAll({
      files: message?.files ?? EMPTY_LIST,
      onSetModal: setModal,
    })

  const {
    handleClickDeleteCopiedEntity,
    handleCopyEmailToEntity,
    handleDeleteCopiedEntity,
    handleOpenCopyToModal,
  } = useEmailCopyTo({
    copyToApi,
    deleteCopiedEntityApi,
    id,
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
  })

  async function handleActionSubmit({
    actionFunction,
    addSuccessNotification = handleAddSuccessNotification,
    name,
    params,
    onReload = handleReloadEntity,
  }) {
    return actionFunction(params)
      .then(response => {
        addSuccessNotification(
          EMAIL_PROFILE_ACTIONS_MESSAGE_SUCCESS_TITLE[name]
        )

        onReload()

        return response
      })
      .catch(() => {
        handleAddErrorNotification(
          EMAIL_PROFILE_ACTIONS_MESSAGE_ERROR_TITLE[name]
        )
      })
      .finally(() => {
        setPerformingAction(false)
      })
  }

  function handleSubmitAddNoteMessage(id, values) {
    // api does not accept empty string for this field
    handleUpdate({
      action: EMAIL_PROFILE_ACTION_ADD_NOTE,
      data: { notes: values.notes || null },
      id,
    })
  }

  function handleOpenAddNoteMessageModal() {
    setModal(
      <EmailAddNoteActionModal
        id={id}
        initialValues={getEmailAddNoteInitialValues(message?.notes)}
        onClose={handleCloseModal}
        onSubmit={handleSubmitAddNoteMessage}
      />
    )
  }

  function handleResend() {
    handleActionSubmit({
      actionFunction: resendApi,
      addSuccessNotification: handleAddInfoNotification,
      name: EMAIL_PROFILE_ACTION_MESSAGE_RESEND,
      params: id,
      onReload: handleReloadMessages,
    })
  }

  function handleSubmitCopyEmailToEntity(data) {
    handleActionSubmit({
      actionFunction: handleCopyEmailToEntity,
      name: EMAIL_ACTION_COPY_TO,
      params: data,
    })
  }

  function handleSubmitRemove({ getPreparedData, id }) {
    return () =>
      handleActionSubmit({
        actionFunction: handleDeleteCopiedEntity,
        name: EMAIL_ACTION_COPY_TO_DELETE,
        params: { getPreparedData, id, uuid: item?.uuid },
      })
  }

  function handleClickRemoveCopiedEntity({ event, getPreparedData, id }) {
    stopPropagation(event)

    handleClickDeleteCopiedEntity({
      onSubmit: handleSubmitRemove({ getPreparedData, id }),
    })
  }

  function handleClickMessageAction({ event, value }) {
    switch (value) {
      case EMAIL_ACTION_CREATE_SERVICE_REQUEST:
        handleOpenRequestCreationModal(message)
        break

      case EMAIL_ACTION_ATTACH_REQUEST:
        handleOpenAttachServiceRequestModal({ getExcludeIds, item: message })
        break

      case EMAIL_PROFILE_ACTION_MESSAGE_RESEND:
        handleResend()
        break

      case EMAIL_ACTION_VIEW_ENTIRE_MESSAGE:
        handleClick({
          event,
          link: getRawPageLink({ id }),
          target: HTML_LINK_TARGET.blank,
        })
        break

      case EMAIL_ACTION_COPY_TO:
        handleOpenCopyToModal({
          item: message,
          onSubmit: handleSubmitCopyEmailToEntity,
        })
        break

      case EMAIL_PROFILE_ACTION_DOWNLOAD_FILES:
        handleClickDownloadAllMessage()
        break

      case EMAIL_PROFILE_ACTION_ADD_NOTE:
        handleOpenAddNoteMessageModal()
        break

      default:
        handleClickAction({
          id,
          item: message,
          value,
          onArchive: deleteMessageApi,
        })
        break
    }
  }

  function handleViewEntireMessage(event) {
    handleClickMessageAction({ event, value: EMAIL_ACTION_VIEW_ENTIRE_MESSAGE })
  }

  return {
    handleClickAction: handleClickMessageAction,
    handleClickRemoveCopiedEntity,
    handleUpdate,
    handleViewEntireMessage,
    requestCreationModal,
  }
}
